/**
 * TermsModal Component
 *
 * Full-screen modal for displaying and accepting Terms of Service
 * - Requires scrolling to bottom before enabling checkboxes
 * - Three mandatory checkboxes for legal acknowledgments
 * - Captures signatory title
 * - Cannot be dismissed without action (Accept or Decline)
 */

import { useState, useRef, useEffect } from 'react';
import { TERMS_OF_SERVICE, TERMS_VERSION, EFFECTIVE_DATE } from '../data/termsOfService';
import ReactMarkdown from 'react-markdown';

export default function TermsModal({
  isOpen,
  onAccept,
  onDecline,
  companyName,
  signatoryName,
  email,
}) {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [signatoryTitle, setSignatoryTitle] = useState('');
  const [checkboxes, setCheckboxes] = useState({
    orgBinding: false,
    conveyanceDuty: false,
    termsAcceptance: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const termsContentRef = useRef(null);

  // Check if user has scrolled to bottom
  const handleScroll = () => {
    if (!termsContentRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = termsContentRef.current;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight - 50; // 50px threshold

    if (scrolledToBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  // Reset scroll detection when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasScrolledToBottom(false);
      setCheckboxes({
        orgBinding: false,
        conveyanceDuty: false,
        termsAcceptance: false,
      });
      setSignatoryTitle('');
    }
  }, [isOpen]);

  const handleCheckboxChange = (name) => {
    setCheckboxes((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const allCheckboxesChecked =
    checkboxes.orgBinding &&
    checkboxes.conveyanceDuty &&
    checkboxes.termsAcceptance;

  const canAccept =
    hasScrolledToBottom &&
    allCheckboxesChecked &&
    signatoryTitle.trim().length > 0;

  const handleAccept = async () => {
    if (!canAccept) return;

    setIsSubmitting(true);
    try {
      await onAccept({
        signatoryTitle: signatoryTitle.trim(),
        acknowledgedOrgBinding: checkboxes.orgBinding,
        acknowledgedConveyanceDuty: checkboxes.conveyanceDuty,
        acknowledgedTerms: checkboxes.termsAcceptance,
      });
    } catch (error) {
      console.error('Error accepting terms:', error);
      setIsSubmitting(false);
    }
  };

  const handleDecline = () => {
    if (window.confirm(
      'If you decline the Terms of Service, you will not be able to create an account. Are you sure you want to decline?'
    )) {
      onDecline();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Terms of Service</h1>
          <p style={styles.subtitle}>
            Please read carefully. You are accepting these terms on behalf of your organization.
          </p>
        </div>

        {/* Terms Content - Scrollable */}
        <div
          ref={termsContentRef}
          onScroll={handleScroll}
          style={styles.termsContent}
        >
          <div style={styles.legalNotice}>
            <strong>⚠️ IMPORTANT LEGAL NOTICE</strong>
            <p>
              This is a legally binding agreement. By accepting, you are waiving certain rights
              including the right to jury trial and class action lawsuits. You agree to resolve
              disputes through mediation and arbitration in Guilford County, North Carolina.
            </p>
          </div>

          <div style={styles.markdownContent}>
            <ReactMarkdown>{TERMS_OF_SERVICE}</ReactMarkdown>
          </div>

          {!hasScrolledToBottom && (
            <div style={styles.scrollPrompt}>
              ↓ Please scroll to the bottom to continue ↓
            </div>
          )}
        </div>

        {/* Acceptance Form */}
        <div style={styles.formSection}>
          <div style={styles.signatoryInfo}>
            <div style={styles.infoRow}>
              <strong>Company:</strong> <span>{companyName}</span>
            </div>
            <div style={styles.infoRow}>
              <strong>Signatory:</strong> <span>{signatoryName}</span>
            </div>
            <div style={styles.infoRow}>
              <strong>Email:</strong> <span>{email}</span>
            </div>
          </div>

          {/* Signatory Title Input */}
          <div style={styles.formGroup}>
            <label htmlFor="signatoryTitle" style={styles.label}>
              Your Title/Role at {companyName} <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="signatoryTitle"
              value={signatoryTitle}
              onChange={(e) => setSignatoryTitle(e.target.value)}
              placeholder="e.g., Owner, President, CEO, Manager"
              style={styles.input}
              disabled={!hasScrolledToBottom}
            />
          </div>

          {/* Legal Acknowledgment Checkboxes */}
          <div style={styles.checkboxSection}>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={checkboxes.orgBinding}
                  onChange={() => handleCheckboxChange('orgBinding')}
                  disabled={!hasScrolledToBottom}
                  style={styles.checkbox}
                />
                <span>
                  <strong>I confirm I have the authority to bind {companyName} to these Terms of Service.</strong>
                  <br />
                  <small style={styles.checkboxHelpText}>
                    You represent that you are authorized to enter into legally binding agreements on behalf of your organization.
                  </small>
                </span>
              </label>
            </div>

            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={checkboxes.conveyanceDuty}
                  onChange={() => handleCheckboxChange('conveyanceDuty')}
                  disabled={!hasScrolledToBottom}
                  style={styles.checkbox}
                />
                <span>
                  <strong>I understand I must convey all disclaimers and waivers to all team members and users</strong> of the Krinos application.
                  <br />
                  <small style={styles.checkboxHelpText}>
                    You are legally required to inform all employees, contractors, and downstream users of the limitations and disclaimers contained in these Terms.
                  </small>
                </span>
              </label>
            </div>

            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={checkboxes.termsAcceptance}
                  onChange={() => handleCheckboxChange('termsAcceptance')}
                  disabled={!hasScrolledToBottom}
                  style={styles.checkbox}
                />
                <span>
                  <strong>I have read and agree to the Terms of Service,</strong> including:
                  <br />
                  <small style={styles.checkboxHelpText}>
                    • Beta software disclaimer and "AS-IS" warranty waiver
                    <br />
                    • Requirement to independently verify all calculations
                    <br />
                    • Limitation of liability and hold harmless provisions
                    <br />
                    • Mediation and arbitration in Guilford County, North Carolina
                    <br />
                    • Waiver of jury trial and class action rights
                  </small>
                </span>
              </label>
            </div>
          </div>

          {!hasScrolledToBottom && (
            <div style={styles.scrollWarning}>
              ⚠️ Please scroll to the bottom of the Terms of Service to enable acceptance.
            </div>
          )}

          {/* Action Buttons */}
          <div style={styles.buttonGroup}>
            <button
              onClick={handleDecline}
              style={styles.declineButton}
              disabled={isSubmitting}
            >
              Decline
            </button>
            <button
              onClick={handleAccept}
              style={{
                ...styles.acceptButton,
                ...(canAccept ? {} : styles.acceptButtonDisabled),
              }}
              disabled={!canAccept || isSubmitting}
            >
              {isSubmitting ? 'Processing...' : 'I Accept'}
            </button>
          </div>

          <div style={styles.legalFooter}>
            <small>
              Version {TERMS_VERSION} • Effective {EFFECTIVE_DATE}
              <br />
              By clicking "I Accept," you are legally binding {companyName} to these terms.
              <br />
              Your acceptance will be recorded with timestamp and IP address for legal verification.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    width: '100%',
    maxWidth: '900px',
    maxHeight: '95vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 32px',
    borderBottom: '2px solid #e5e7eb',
    backgroundColor: '#f9fafb',
    flexShrink: 0,
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  termsContent: {
    minHeight: '350px',
    maxHeight: '50vh',
    overflowY: 'auto',
    padding: '24px 32px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    position: 'relative',
    flexShrink: 0,
  },
  legalNotice: {
    backgroundColor: '#fef3c7',
    border: '2px solid #f59e0b',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '24px',
    fontSize: '14px',
    color: '#92400e',
  },
  markdownContent: {
    fontSize: '13px',
    lineHeight: '1.7',
    color: '#374151',
  },
  scrollPrompt: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#3b82f6',
    color: '#ffffff',
    textAlign: 'center',
    padding: '12px',
    fontSize: '14px',
    fontWeight: '600',
    animation: 'pulse 2s infinite',
  },
  formSection: {
    padding: '20px 32px',
    backgroundColor: '#f9fafb',
    maxHeight: '40vh',
    overflowY: 'auto',
    flexShrink: 0,
  },
  signatoryInfo: {
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  infoRow: {
    marginBottom: '8px',
    display: 'flex',
    gap: '8px',
  },
  formGroup: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  required: {
    color: '#dc2626',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  checkboxSection: {
    marginBottom: '12px',
  },
  checkboxGroup: {
    marginBottom: '12px',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px',
  },
  checkboxLabel: {
    display: 'flex',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#1f2937',
  },
  checkbox: {
    marginTop: '3px',
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  checkboxHelpText: {
    color: '#6b7280',
    fontSize: '12px',
    display: 'block',
    marginTop: '6px',
  },
  scrollWarning: {
    backgroundColor: '#fef3c7',
    border: '1px solid #f59e0b',
    borderRadius: '6px',
    padding: '12px',
    fontSize: '13px',
    color: '#92400e',
    textAlign: 'center',
    marginBottom: '16px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  declineButton: {
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#6b7280',
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  acceptButton: {
    padding: '12px 32px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  acceptButtonDisabled: {
    backgroundColor: '#d1d5db',
    cursor: 'not-allowed',
  },
  legalFooter: {
    marginTop: '16px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '11px',
    lineHeight: '1.6',
  },
};
